import {
  type ProductionPerformanceValuesResponse,
  type ProductionRequestPayload,
  type ProductionPerformanceValuesRequestPayload,
  formatDateUtcToTimezoneDateOnly,
  PERFORMANCE_KPI_REQUEST_STALE_TIME
} from '@domain/kpis'
import {ApiClient} from '@hconnect/apiclient'
import {getSystemTimezone} from '@hconnect/common/utils'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'

import {mapProductionPerformanceValuesDto} from '../../mappers'
import {type ProductionPerformanceValues} from '../../types'

const QueryKey = 'production-performance-values'

export const useProductionPerformanceValues = (
  {timeRange, locationId, yearOffset = 3}: ProductionRequestPayload,
  options?: Omit<
    UseQueryOptions<ProductionPerformanceValuesResponse, AxiosError, ProductionPerformanceValues>,
    'queryKey' | 'queryFn'
  >
) => {
  const localDateOnlyRange = {
    startDate: formatDateUtcToTimezoneDateOnly(timeRange.startDate, getSystemTimezone()),
    endDate: formatDateUtcToTimezoneDateOnly(timeRange.endDate, getSystemTimezone())
  }

  return useQuery<ProductionPerformanceValuesResponse, AxiosError, ProductionPerformanceValues>({
    queryKey: [QueryKey, localDateOnlyRange.startDate, localDateOnlyRange.endDate, locationId],
    queryFn: async ({signal}) => {
      const payload: ProductionPerformanceValuesRequestPayload = {
        dateRange: localDateOnlyRange,
        yearOffset
      }

      const response = await ApiClient.axiosInstance.post<ProductionPerformanceValuesResponse>(
        '/kpi/dashboard/production-performance-values/',
        payload,
        {params: {locationId}, signal}
      )

      return response.data
    },
    select: (data) => mapProductionPerformanceValuesDto(data),
    staleTime: PERFORMANCE_KPI_REQUEST_STALE_TIME,
    ...options
  })
}
